// NOTICE: KAPTION-INTEGRATION
// Composable for managing scheduled messages in a conversation
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import KaptionScheduledMessagesAPI from 'dashboard/api/kaptionScheduledMessages';

/**
 * Composable for managing scheduled messages per conversation
 *
 * @param {import('vue').Ref<number>} conversationId - The conversation ID ref
 * @returns {Object} State and methods for scheduled messages
 */
export function useKaptionScheduledMessages(conversationId) {
  const store = useStore();
  const scheduledMessages = ref([]);
  const isFetching = ref(false);
  const isCancelling = ref(false);

  const accountId = computed(() => store.getters.getCurrentAccountId);

  const scheduledMessagesCount = computed(() => scheduledMessages.value.length);

  const fetchScheduledMessages = async () => {
    if (!conversationId.value || !accountId.value) {
      scheduledMessages.value = [];
      return;
    }

    isFetching.value = true;
    try {
      const response = await KaptionScheduledMessagesAPI.getScheduledMessages(
        accountId.value,
        conversationId.value
      );
      // The API returns { scheduled_messages: [...] }
      scheduledMessages.value = response.data.scheduled_messages || [];
    } catch (error) {
      // Silent failure
      scheduledMessages.value = [];
    } finally {
      isFetching.value = false;
    }
  };

  const cancelScheduledMessage = async messageId => {
    if (!conversationId.value || !accountId.value) return;

    isCancelling.value = true;
    try {
      await KaptionScheduledMessagesAPI.cancelScheduledMessage(
        accountId.value,
        conversationId.value,
        messageId
      );
      await fetchScheduledMessages();
    } finally {
      isCancelling.value = false;
    }
  };

  // Watch for conversationId or accountId changes
  watch(
    [conversationId, accountId],
    () => {
      fetchScheduledMessages();
    },
    { immediate: true }
  );

  // Listen for 'kaption-message-scheduled' event to refresh the list
  const handleMessageScheduled = event => {
    if (event.conversationId === conversationId.value) {
      fetchScheduledMessages();
    }
  };

  onMounted(() => {
    emitter.on('kaption-message-scheduled', handleMessageScheduled);
  });

  onUnmounted(() => {
    emitter.off('kaption-message-scheduled', handleMessageScheduled);
  });

  return {
    scheduledMessages,
    scheduledMessagesCount,
    isFetching,
    isCancelling,
    refresh: fetchScheduledMessages,
    cancelScheduledMessage,
  };
}
